import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, CheckCircle2, Clock, RotateCcw, BookOpen, LayoutDashboard, MessageSquareText, Compass } from "lucide-react";
import { http } from "@/lib/api";
import { Card, btnPrimary, btnGhost } from "@/components/portal/ui";

const pct = (s) => `${Math.round(s * 100)}%`;
const day = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "");
export const COURSE_INFO = {
  "is-101": { code: "IS-101", title: "Foundations of Infant Sleep", price: "$99", faculty: "$299" },
  "ns-101": { code: "NS-101", title: "Naps, Wake Windows & Daily Rhythm", price: "$149", faculty: "$349" },
  "is-201": { code: "IS-201", title: "Night Wakings & Independent Settling", price: "$299", faculty: "$399" },
  "is-301": { code: "IS-301", title: "Sleep Regressions & Developmental Changes", price: "$299", faculty: "$399" },
  "is-401": { code: "IS-401", title: "Bedtime, Early Waking & the Crib Transition", price: "$299", faculty: "$399" },
};

function Remarks({ f, testid }) {
  return (
    <div className="mt-5 border-l-2 border-[#BDA677] pl-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.14em] text-[#8C7444] mb-1 flex items-center gap-1.5"><MessageSquareText size={13} /> Instructor remarks · {f.reviewed_by}</div>
      <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{f.remarks}</p>
      <p className="text-xs opacity-70 mt-1">Released {day(f.released_at)}</p>
    </div>
  );
}

export function PendingReview({ f }) {
  return (
    <Card className="p-6 mt-6 border-t-2 border-t-[#BDA677]" data-testid="final-pending">
      <div className="flex items-center gap-2 text-[#8C7444] text-xs uppercase tracking-[0.14em]"><Clock size={14} /> Pending Faculty Review</div>
      <p className="font-academic font-semibold text-2xl text-[#172A3A] mt-2">Final Assessment Submitted — Awaiting Faculty Review.</p>
      <p className="text-[15px] text-[#4F5B67] mt-2">Your instructor will review your final assessment and release your results and feedback. You'll be notified when your review is available.</p>
      <p className="text-xs text-[#5A6470] mt-3">Submitted {day(f.submitted_at)} · Attempt {f.attempt}</p>
    </Card>
  );
}

export function ReleasedNotPassed({ c, onRetry }) {
  const f = c.final_state;
  return (
    <Card className="p-6 mt-6 border-t-2 border-t-[#9E4F4F]" data-testid="final-not-passed">
      <div className="text-xs uppercase tracking-[0.14em] text-[#8a3f3f]">Final assessment released</div>
      <p className="font-academic font-semibold text-2xl text-[#172A3A] mt-2">Final Assessment Reviewed — Additional Work Required.</p>
      <p className="text-[15px] text-[#2B3A48] mt-3" data-testid="final-released-grade">Released grade: <strong>{f.grade} of {f.max}</strong> · {pct(f.pct)} · Passing requirement: {c.pass_requirement}</p>
      <Remarks f={f} testid="final-remarks" />
      <p className="text-sm text-[#4F5B67] mt-4">Review the lessons and your instructor's remarks, then retry. Your earlier grades, remarks and submissions stay in your academic record.</p>
      <div className="flex flex-wrap gap-3 mt-5">
        <button onClick={() => document.querySelector("[data-testid='final-remarks']")?.scrollIntoView({ behavior: "smooth" })} data-testid="final-review-feedback" className={btnGhost}>Review Feedback</button>
        <button onClick={onRetry} data-testid="final-retry" className={`${btnPrimary} inline-flex items-center gap-2`}><RotateCcw size={15} /> Retry Final Assessment</button>
      </div>
    </Card>
  );
}

export function PassedAwaitingModules({ c, missing, onOpen }) {
  const f = c.final_state;
  return (
    <Card className="p-6 mt-6 border-t-2 border-t-[#BDA677]" data-testid="final-passed-incomplete">
      <p className="font-academic font-semibold text-2xl text-[#172A3A]">Final Assessment Passed — Remaining Modules Required.</p>
      <p className="text-[15px] text-[#2B3A48] mt-2">Released grade: <strong>{f.grade} of {f.max}</strong> · {pct(f.pct)} · Passed</p>
      <Remarks f={f} testid="final-remarks" />
      <p className="text-sm text-[#4F5B67] mt-4">Complete these modules to finish the course:</p>
      <ul className="mt-2 space-y-1">{missing.map((l) => <li key={l.key}><button onClick={() => onOpen(l.key)} className="text-[#7A6438] underline underline-offset-4 text-sm">Lesson {l.number}: {l.title}</button></li>)}</ul>
    </Card>
  );
}

function NextSteps({ c, onSwitch }) {
  const [access, setAccess] = useState({});
  useEffect(() => { http.get("/portal/courses/access").then((r) => setAccess(r.data.courses || {})).catch(() => {}); }, []);
  const n = COURSE_INFO[c.next_course];
  const owned = access[c.next_course]?.access;
  const opt = "bg-[#FBF9F4] border border-[#E3DCCB] rounded-xl p-5 flex flex-col";
  return (
    <section className="mt-8" data-testid="next-steps">
      <h2 className="font-academic font-semibold text-2xl text-[#172A3A]">Where Would You Like to Continue Your Studies?</h2>
      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        <div className={opt} data-testid="next-explore">
          <Compass size={20} className="text-[#8C7444]" />
          <p className="font-academic font-semibold text-lg text-[#172A3A] mt-2">Explore My Next Course</p>
          <p className="text-sm text-[#4F5B67] mt-1 flex-1">Suggested continuation: <strong>{n.code} {n.title}</strong>. Optional — every course can be studied independently.</p>
          {owned ? <button onClick={() => onSwitch(c.next_course)} data-testid="next-open-owned" className={`${btnPrimary} mt-4`}>Continue to {n.code}</button> : <>
            <p className="text-xs text-[#5A6470] mt-2" data-testid="next-tuition">Independent Study {n.price} · Faculty-Led {n.faculty}</p>
            <Link to={`/courses/${c.next_course}`} data-testid="next-compare" className={`${btnPrimary} mt-4 text-center`}>Compare Study Options</Link></>}
        </div>
        <div className={opt}>
          <BookOpen size={20} className="text-[#8C7444]" />
          <p className="font-academic font-semibold text-lg text-[#172A3A] mt-2">Review My Completed Course</p>
          <p className="text-sm text-[#4F5B67] mt-1 flex-1">Revisit your lessons, assessment results and Andrea's feedback at any time.</p>
          <button onClick={() => document.getElementById("course-syllabus-h")?.scrollIntoView({ behavior: "smooth" })} data-testid="next-review" className={`${btnGhost} mt-4`}>Review My Course</button>
        </div>
        <div className={opt}>
          <LayoutDashboard size={20} className="text-[#8C7444]" />
          <p className="font-academic font-semibold text-lg text-[#172A3A] mt-2">Return to My Student Dashboard</p>
          <p className="text-sm text-[#4F5B67] mt-1 flex-1">Your completed course status is shown on your dashboard.</p>
          <Link to="/portal" data-testid="next-dashboard" className={`${btnGhost} mt-4 text-center`}>My Student Dashboard</Link>
        </div>
      </div>
    </section>
  );
}

export function CourseCompleted({ c, onSwitch }) {
  const f = c.final_state;
  return (
    <section className="mt-6" data-testid="course-completed">
      <div className="bg-[#172A3A] text-[#F5F2E9] rounded-2xl p-7 sm:p-10 border-2 border-[#BDA677] animate-in fade-in slide-in-from-bottom-2 duration-500">
        <div className="flex items-center gap-2 text-[#BDA677] text-xs uppercase tracking-[0.22em]"><Award size={18} /> <span data-testid="course-completed-badge">Course Completed</span></div>
        <h2 className="font-academic font-semibold text-3xl sm:text-4xl mt-3 leading-tight" data-testid="course-completed-title">Congratulations! You've Successfully Completed {c.code} — {c.title}.</h2>
        {c.student_name && <p className="mt-3 text-[#E8E1CF]" data-testid="course-completed-student">{c.student_name}</p>}
        <div className="h-px w-24 bg-[#BDA677] my-5" />
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          {[["Modules", `${c.lessons.length} of ${c.lessons.length} completed`, "modules"], ["Final assessment", `${f.grade} of ${f.max} · ${pct(f.pct)}`, "grade"],
            ["Status", "Passed · 100% progress", "status"], ["Completed", day(c.completed_at), "date"]].map(([k, v, t]) => (
            <div key={k}><dt className="text-[#BDA677] text-xs uppercase tracking-[0.12em]">{k}</dt><dd className="mt-1 flex items-center gap-1.5" data-testid={`course-completed-${t}`}>{t === "status" && <CheckCircle2 size={15} className="text-[#BDA677]" />}{v}</dd></div>))}
        </dl>
        <Remarks f={f} testid="course-completed-remarks" />
        <Link to={`/certificate/${c.slug}`} data-testid="course-certificate" className="mt-6 inline-flex items-center gap-2 bg-[#BDA677] text-[#172A3A] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#cdb98d] transition-colors"><Award size={16} /> View, Print & Share My Certificate</Link>
      </div>
      <NextSteps c={c} onSwitch={onSwitch} />
    </section>
  );
}
